import React, { useCallback, useEffect, useState } from 'react';
import { Button, Badge, Spinner, Alert, Form } from 'react-bootstrap';
import SettingsSelect from './SettingsSelect';
import { toast } from 'sonner';
import { Ban, Trash2, Plus } from 'lucide-react';

import {
    getBlocklist,
    addBlocklistEntry,
    deleteBlocklistEntry,
} from '../../../services/InboxService';

// Match-type options for the react-select dropdown (same component used on
// the time-reporting page).
const MATCH_TYPE_OPTIONS = [
    { value: 'email', label: 'E-postadress' },
    { value: 'domain', label: 'Domän' },
];

/**
 * InboxBlocklistSettings — manage the per-tenant sender suppression list.
 *
 * Rules are either an exact email address or a whole domain. Incoming email
 * from a matching sender is auto-dismissed at intake (and items already
 * pulled before the rule was added are hidden too). Delete a rule to let the
 * sender through again.
 *
 * Uses the same `modern-card` design language as the other CompanySettings
 * tabs (those styles are defined in CompanySettings' inline <style> block,
 * which is global once that page renders).
 */
export default function InboxBlocklistSettings({ companyId }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Add-rule form
    const [matchType, setMatchType] = useState('email');
    const [value, setValue] = useState('');
    const [notes, setNotes] = useState('');
    const [adding, setAdding] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const load = useCallback(async () => {
        if (!companyId) return;
        try {
            setLoading(true);
            const data = await getBlocklist(companyId);
            setEntries(data?.entries || []);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.error || err.message || 'Kunde inte hämta blocklistan');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        load();
    }, [load]);

    const onAdd = useCallback(async (e) => {
        e.preventDefault();
        const trimmed = value.trim();
        if (!trimmed) return;
        setAdding(true);
        try {
            const entry = await addBlocklistEntry(companyId, { matchType, value: trimmed, notes: notes.trim() || null });
            const hidden = entry?.hidden || 0;
            toast.success(hidden > 0 ? `Regel tillagd — ${hidden} befintliga objekt dolda` : 'Regel tillagd');
            setValue('');
            setNotes('');
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.error || err.message || 'Kunde inte lägga till regeln');
        } finally {
            setAdding(false);
        }
    }, [companyId, matchType, value, notes, load]);

    const onDelete = useCallback(async (id) => {
        setDeletingId(id);
        try {
            await deleteBlocklistEntry(companyId, id);
            toast.success('Regel borttagen');
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.error || err.message || 'Kunde inte ta bort regeln');
        } finally {
            setDeletingId(null);
        }
    }, [companyId, load]);

    return (
        <div className="modern-card">
            <div className="modern-card-header">
                <div>
                    <h2 className="section-title">
                        <Ban size={16} />
                        Blockerade avsändare
                    </h2>
                    <p className="section-subtitle">
                        E-post från blockerade avsändare avfärdas automatiskt vid mottagning.
                        Ta bort en regel för att släppa in avsändaren igen.
                    </p>
                </div>
            </div>

            <div className="modern-card-body">
                {/* Add-rule form */}
                <Form onSubmit={onAdd}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                        <Form.Group style={{ width: 200 }}>
                            <Form.Label className="modern-label">Typ</Form.Label>
                            <SettingsSelect
                                options={MATCH_TYPE_OPTIONS}
                                value={matchType}
                                onChange={(v) => setMatchType(v || 'email')}
                                aria-label="Typ av regel"
                            />
                        </Form.Group>
                        <Form.Group style={{ flex: 1, minWidth: 220 }}>
                            <Form.Label className="modern-label">
                                {matchType === 'email' ? 'Adress' : 'Domän'}
                            </Form.Label>
                            <input
                                className="modern-input"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder={matchType === 'email' ? 'foo@example.com' : 'example.com'}
                            />
                        </Form.Group>
                        <Form.Group style={{ flex: 1, minWidth: 180 }}>
                            <Form.Label className="modern-label">Anteckning (valfritt)</Form.Label>
                            <input
                                className="modern-input"
                                value={notes}
                                maxLength={255}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="t.ex. Spam"
                            />
                        </Form.Group>
                        <Button
                            type="submit"
                            variant="primary"
                            className="d-flex align-items-center gap-2"
                            disabled={adding || !value.trim()}
                        >
                            {adding ? <Spinner animation="border" size="sm" /> : <Plus size={16} />}
                            Lägg till
                        </Button>
                    </div>
                </Form>

                {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

                {/* Rules list — a simple row per blocked sender, no table. */}
                <div className="mt-4">
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" /> <span className="ms-2">Laddar…</span>
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="empty-state">
                            <Ban size={32} className="empty-icon" />
                            <h4>Inga blockerade avsändare</h4>
                            <p>Lägg till en e-postadress eller domän ovan för att börja blockera.</p>
                        </div>
                    ) : (
                        // The shared settings row, like the users and templates
                        // lists — the hand-rolled flex row here predated it.
                        <ul className="settings-rows">
                            {entries.map((entry) => (
                                <li key={entry.id} className="settings-row blocklist-row">
                                    <span className="blocklist-row__value" title={entry.notes || ''}>
                                        {entry.match_type === 'domain' ? `@${entry.value}` : entry.value}
                                    </span>
                                    {entry.notes && (
                                        <span className="blocklist-row__note">{entry.notes}</span>
                                    )}
                                    <Badge bg="light" text="dark" className="blocklist-row__kind">
                                        {entry.match_type === 'domain' ? 'Domän' : 'Adress'}
                                    </Badge>
                                    <span className="settings-icon-actions ms-auto">
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => onDelete(entry.id)}
                                            disabled={deletingId === entry.id}
                                            title="Ta bort"
                                        >
                                            {deletingId === entry.id
                                                ? <Spinner animation="border" size="sm" />
                                                : <Trash2 size={14} />}
                                        </Button>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
